import { useState, useRef, useCallback, useEffect } from "react";

/**
 * usePullToRefresh — Native-feel pull-to-refresh for mobile dashboards.
 * Returns: { pullRef, pullIndicator, refreshing }
 * Attach pullRef to the scrollable container, render pullIndicator above content.
 */
export default function usePullToRefresh(onRefresh, { threshold = 80, disabled = false } = {}) {
  const [pulling, setPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef(0);
  const pullRef = useRef(null);

  const handleTouchStart = useCallback((e) => {
    if (disabled || refreshing) return;
    if (window.scrollY > 5) return; // only trigger at top
    startY.current = e.touches[0].clientY;
    setPulling(true);
  }, [disabled, refreshing]);

  const handleTouchMove = useCallback((e) => {
    if (!pulling || disabled || refreshing) return;
    const dy = Math.max(0, e.touches[0].clientY - startY.current);
    // Dampen the pull distance
    setPullDistance(Math.min(dy * 0.5, threshold * 1.5));
  }, [pulling, disabled, refreshing, threshold]);

  const handleTouchEnd = useCallback(async () => {
    if (!pulling || disabled) return;
    setPulling(false);
    if (pullDistance >= threshold && onRefresh) {
      setRefreshing(true);
      setPullDistance(threshold * 0.5);
      try {
        await onRefresh();
      } catch (e) { /* silently fail */ }
      setRefreshing(false);
    }
    setPullDistance(0);
  }, [pulling, pullDistance, threshold, onRefresh, disabled]);

  useEffect(() => {
    const el = pullRef.current || window;
    el.addEventListener("touchstart", handleTouchStart, { passive: true });
    el.addEventListener("touchmove", handleTouchMove, { passive: true });
    el.addEventListener("touchend", handleTouchEnd, { passive: true });
    return () => {
      el.removeEventListener("touchstart", handleTouchStart);
      el.removeEventListener("touchmove", handleTouchMove);
      el.removeEventListener("touchend", handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  const progress = Math.min(pullDistance / threshold, 1);
  const showIndicator = pullDistance > 10 || refreshing;

  const pullIndicator = showIndicator ? (
    <div
      className="flex items-center justify-center overflow-hidden transition-all duration-200 ease-out"
      style={{ height: refreshing ? 48 : pullDistance > 10 ? pullDistance * 0.6 : 0, opacity: progress }}
      data-testid="pull-to-refresh-indicator"
    >
      <div className={`flex items-center gap-2 text-xs font-medium ${refreshing ? "text-emerald-600" : "text-[#888]"}`}>
        <svg
          className={`h-5 w-5 ${refreshing ? "animate-spin text-emerald-600" : "text-[#aaa]"}`}
          style={{ transform: refreshing ? undefined : `rotate(${progress * 360}deg)` }}
          viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
        >
          <path d="M21 12a9 9 0 1 1-6.219-8.56" />
        </svg>
        <span>{refreshing ? "Refreshing..." : progress >= 1 ? "Release to refresh" : "Pull to refresh"}</span>
      </div>
    </div>
  ) : null;

  return { pullRef, pullIndicator, refreshing };
}
